/**
 * BottomTabBar — Floating pill-shaped mobile navigation
 *
 * Centered Aurora pill with the locked 5-label IA.
 *
 * @see Figma_assets/iOS/Component 2@2x.png for visual reference
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@ownyou/ui-design-system';
import { NAV_ITEMS, NAV_ICONS } from './nav-constants';

export interface BottomTabBarProps {
  className?: string;
}

function isActiveRoute(pathname: string, itemPath: string): boolean {
  if (itemPath === '/') {
    return pathname === '/';
  }
  return pathname.startsWith(itemPath);
}

export function BottomTabBar({ className }: BottomTabBarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <nav
      data-testid="bottom-tab-bar"
      role="navigation"
      aria-label="Main navigation"
      className={cn(
        'fixed bottom-6 left-1/2 -translate-x-1/2 z-50',
        'flex items-center justify-around gap-2',
        'w-[min(94vw,390px)] min-h-[64px] px-3 py-2',
        'border rounded-full',
        'shadow-lg',
        className,
      )}
      style={{
        background: 'var(--aurora-card-solid)',
        borderColor: 'var(--aurora-ink-line)',
        boxShadow: 'var(--aurora-card-shadow)',
      }}
    >
      {NAV_ITEMS.map((item) => {
        const active = isActiveRoute(location.pathname, item.path);
        const Icon = NAV_ICONS[item.id];

        return (
          <button
            key={item.id}
            data-testid={`tab-${item.id}`}
            type="button"
            role="tab"
            aria-current={active ? 'page' : undefined}
            aria-label={item.label}
            onClick={() => navigate(item.path)}
            className={cn(
              'flex min-w-0 flex-1 flex-col items-center justify-center gap-1',
              'h-12 rounded-full px-1',
              'transition-all duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
            )}
            style={{
              background: active ? 'color-mix(in srgb, var(--aurora-accent) 14%, transparent)' : undefined,
            }}
          >
            <Icon
              className="w-5 h-5 shrink-0"
              style={{
                color: active ? 'var(--aurora-accent)' : 'var(--aurora-ink-soft)',
                opacity: 1,
              }}
            />
            <span
              className="max-w-full truncate text-[10px] font-medium leading-none"
              style={{
                color: active ? 'var(--aurora-ink)' : 'var(--aurora-ink-dim)',
                fontFamily: 'var(--aurora-font-ui)',
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
